/**
 * Workflow — a two-column "process" section (DS core). A heading sits on the
 * left (sticky on desktop); on the right a column of cards that STACK on top of
 * each other as you scroll (each is `position: sticky` at a slightly larger top
 * offset, so they pile into a deck with each card's top edge peeking — the 2ZUP
 * reference effect). Stacks to a single column on mobile.
 *
 * Brought into our DS: cards use our surface (grey + white 8px border, no
 * shadow), our type scale, and an optional accent (blue) CTA card as the last
 * one. Content-agnostic. Pure-CSS sticky stack (works with Lenis). No raw [#hex].
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { CardHeading } from "./Heading";

export type WorkflowStep = { n: string; title: ReactNode; body: ReactNode; media?: ReactNode };

/** Where the first card sticks (px from viewport top) and how much each later
 *  card is pushed down, leaving the previous card's top edge visible. */
const STICK_TOP = 100;
const PEEK = 16;

/** Shared card surface — matches BentoCard (grey, white 8px border, no shadow). */
const CARD_BASE = "rounded-3xl border-8 border-white p-4 md:p-7 font-ds";

type StepCard = { kind: "step"; n: string; title: ReactNode; body: ReactNode; media?: ReactNode };
type CtaCard = { kind: "cta"; title: ReactNode; body?: ReactNode; action?: ReactNode };

export function Workflow({
  eyebrow,
  title,
  description,
  note,
  steps,
  cta,
}: {
  /** Pass our <Badge>/<Eyebrow> so it matches the rest of the site. */
  eyebrow?: ReactNode;
  /** Heading; wrap the emphasised part in <span className="text-ds-ink">. */
  title: ReactNode;
  /** Supporting paragraph under the heading. */
  description?: ReactNode;
  /** Optional fine print rendered under the description, inside the head. */
  note?: ReactNode;
  steps: WorkflowStep[];
  /** Optional accent (blue) card rendered last in the stack. */
  cta?: { title: ReactNode; body?: ReactNode; action?: ReactNode };
}) {
  const cards: (StepCard | CtaCard)[] = [
    ...steps.map((s): StepCard => ({ kind: "step", n: s.n, title: s.title, body: s.body, media: s.media })),
    ...(cta ? [{ kind: "cta", ...cta } as CtaCard] : []),
  ];

  // While the section owns the viewport centre, flood the background with the
  // bright brand blue (and the head text/tag flip to white via the .wf-active
  // CSS); revert when it leaves. rootMargin keeps it reliable even though the
  // sticky stack makes the section much taller than one screen.
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setActive(e.isIntersecting), {
      rootMargin: "-40% 0px -40% 0px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className={`relative min-h-screen bg-bg-subtle font-ds ${active ? "wf-active" : ""}`}
    >
      {/* Brand-blue gradient + faint grid, rounded & inset 12px; fades in on active */}
      <div
        className={`pointer-events-none absolute inset-3 overflow-hidden rounded-[32px] transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0"}`}
        style={{ background: "linear-gradient(to bottom, var(--ds-accent-deep), var(--ds-accent))" }}
        aria-hidden
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "linear-gradient(to bottom, black, transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
          }}
        />
      </div>
      <div className="relative mx-auto grid max-w-content gap-12 px-8 py-16 md:px-10 md:py-20 lg:grid-cols-2 lg:gap-16">
        {/* Left — eyebrow + heading (sticky alongside the stacking deck) */}
        <div className="wf-head lg:sticky lg:top-28 lg:self-start">
          {eyebrow}
          <h2 className="mt-5 max-w-[460px] text-balance text-ds-30 font-semibold leading-[1.12] tracking-[-0.5px] text-ds-ink-soft lg:text-ds-34">
            {title}
          </h2>
          {description && (
            <p className="mt-5 max-w-[420px] text-pretty text-ds-18 leading-relaxed text-ds-ink-soft">{description}</p>
          )}
          {note && (
            <p className="mt-20 max-w-[440px] text-[13px] leading-relaxed text-ds-ink-soft">{note}</p>
          )}
        </div>

        {/* Right — sticky-stacking cards */}
        <div className="flex min-w-0 flex-col gap-4">
          {cards.map((c, i) => (
            <div
              key={i}
              className="sticky"
              style={{ top: `${STICK_TOP + i * PEEK}px`, zIndex: i + 1 }}
            >
              {c.kind === "step" ? (
                <div className={`${CARD_BASE} flex flex-col bg-bg-subtle lg:min-h-[360px]`}>
                  <span className="text-ds-46 font-semibold leading-none tracking-[-1px] text-ds-ink/30">{c.n}</span>
                  <div className="mt-5"><CardHeading title={c.title} body={c.body} /></div>
                  {c.media && <div className="mt-auto pt-5">{c.media}</div>}
                </div>
              ) : (
                <div className={`${CARD_BASE} flex flex-col bg-ds-accent lg:min-h-[360px]`}>
                  <h3 className="max-w-[440px] text-balance text-ds-30 font-semibold leading-[1.12] tracking-[-0.5px] text-white lg:text-ds-34">{c.title}</h3>
                  {c.body && <p className="mt-3 max-w-[460px] text-pretty text-ds-18 leading-relaxed text-white/85">{c.body}</p>}
                  {c.action && <div className="mt-auto pt-5">{c.action}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
